import {LIMITS,isSenderSignature,type SignatureAnchor} from '@worki/shared';
export interface InkPoint {x:number;y:number}
export const INK_COLORS=['#111827','#1d4ed8','#15803d','#b91c1c','#7e22ce','#d97706','#db2777'] as const;
export type InkTool='select'|'pen'|'line'|'eraser';
/** Explicit sender editing preserves the annotation-only contract of drawing tools. */
export async function restyleSenderSignature(anchor:SignatureAnchor,color:string):Promise<SignatureAnchor> {
 if(!isSenderSignature(anchor)||anchor.annotation||!anchor.image)return anchor;
 const editable={...anchor,annotation:true};const styled=await restyleInk(editable,{color});
 return styled===editable?anchor:{...anchor,image:styled.image};
}
/** Fixed logical size keeps the selected pen width stable through zoom and export. */
export function inkAnchor(page:number,points:InkPoint[],color:string,width:number,aspect:number):SignatureAnchor|null {
 if(points.length<2) return null;
 const logicalWidth=1000,logicalHeight=logicalWidth/aspect;
 const pad=width+2;
 const x=Math.max(0,Math.min(...points.map(p=>p.x))-pad/logicalWidth);
 const y=Math.max(0,Math.min(...points.map(p=>p.y))-pad/logicalHeight);
 const w=Math.min(1-x,Math.max(.02,Math.max(...points.map(p=>p.x))-x+pad/logicalWidth));
 const h=Math.min(1-y,Math.max(.01,Math.max(...points.map(p=>p.y))-y+pad/logicalHeight));
 const canvas=document.createElement('canvas');canvas.width=Math.max(1,Math.ceil(w*logicalWidth));canvas.height=Math.max(1,Math.ceil(h*logicalHeight));
 const context=canvas.getContext('2d');if(!context) return null;
 context.strokeStyle=color;context.lineWidth=width;context.lineCap='round';context.lineJoin='round';context.beginPath();
 context.moveTo((points[0]!.x-x)*logicalWidth,(points[0]!.y-y)*logicalHeight);
 for(const point of points.slice(1)) context.lineTo((point.x-x)*logicalWidth,(point.y-y)*logicalHeight);
 context.stroke();
 return {annotation:true,page,kind:'signature',x,y,width:w,height:h,filledBy:'sender',image:canvas.toDataURL('image/png')};
}
/** Erase only pixels under the gesture; all other colors and widths remain in the retained PNG. */
export async function eraseInk(anchor:SignatureAnchor,points:InkPoint[],radius:number):Promise<SignatureAnchor|null> {
 if(!anchor.annotation || !anchor.image || !points.some(p=>p.x>=anchor.x-radius&&p.x<=anchor.x+anchor.width+radius&&p.y>=anchor.y-radius&&p.y<=anchor.y+anchor.height+radius)) return anchor;
 const image=await new Promise<HTMLImageElement>((resolve,reject)=>{const value=new Image();value.onload=()=>resolve(value);value.onerror=reject;value.src=anchor.image!;});
 const canvas=document.createElement('canvas');canvas.width=image.naturalWidth;canvas.height=image.naturalHeight;
 const context=canvas.getContext('2d');if(!context) return anchor;
 context.drawImage(image,0,0);context.globalCompositeOperation='destination-out';context.lineCap='round';context.lineJoin='round';
 context.lineWidth=Math.max(1,radius*2*canvas.width/anchor.width);context.beginPath();
 const local=(point:InkPoint)=>({x:(point.x-anchor.x)/anchor.width*canvas.width,y:(point.y-anchor.y)/anchor.height*canvas.height});
 const first=local(points[0]!);context.moveTo(first.x,first.y);
 for(const point of points.slice(1)){const at=local(point);context.lineTo(at.x,at.y);}context.stroke();
 const pixels=context.getImageData(0,0,canvas.width,canvas.height).data;
 if(!pixels.some((value,index)=>index%4===3&&value>0)) return null;
 return {...anchor,image:canvas.toDataURL('image/png')};
}

/** Restyle the retained alpha mask, including legacy PNG-only marks and erased portions. */
export async function restyleInk(anchor:SignatureAnchor,change:{color?:string;widthDelta?:number}):Promise<SignatureAnchor> {
 if(!anchor.annotation || anchor.filledBy!=='sender' || !anchor.image || (!change.color&&!change.widthDelta))return anchor;
 if(!Number.isFinite(change.widthDelta??0)||anchor.width<=0||anchor.height<=0)return anchor;
 try {
  const image=await new Promise<HTMLImageElement>((resolve,reject)=>{const value=new Image();value.onload=()=>resolve(value);value.onerror=reject;value.src=anchor.image!;});
  const width=image.naturalWidth,height=image.naturalHeight;
  if(!width||!height||width*height>16_000_000)return anchor;
  // A one-pixel width change moves each edge by half a logical pixel. Intrinsic
  // pixels can differ from logical pixels after resizing or persisted reloads.
  const radius=Math.abs(change.widthDelta??0)*width/anchor.width/1000/2;
  if(radius>64)return anchor;
  const pixelWidth=anchor.width/width,pixelHeight=anchor.height/height;
  const pad=change.widthDelta!>0?Math.ceil(radius):0;
  const left=Math.min(pad,Math.max(0,Math.floor(anchor.x/pixelWidth)));
  const top=Math.min(pad,Math.max(0,Math.floor(anchor.y/pixelHeight)));
  const right=Math.min(pad,Math.max(0,Math.floor((1-anchor.x-anchor.width)/pixelWidth)));
  const bottom=Math.min(pad,Math.max(0,Math.floor((1-anchor.y-anchor.height)/pixelHeight)));
  const canvas=document.createElement('canvas');canvas.width=width+left+right;canvas.height=height+top+bottom;
  const context=canvas.getContext('2d');if(!context)return anchor;
  context.drawImage(image,left,top);
  if(radius>0){
   const source=context.getImageData(0,0,canvas.width,canvas.height),output=context.createImageData(canvas.width,canvas.height);
   const offsets:{x:number;y:number;weight:number}[]=[];
   const extent=Math.ceil(radius);
   for(let y=-extent;y<=extent;y++)for(let x=-extent;x<=extent;x++){
    const weight=Math.min(1,radius+1-Math.hypot(x,y));
    if(weight>0&&(x||y))offsets.push({x,y,weight});
   }
   let hasInk=false;
   for(let y=0;y<canvas.height;y++)for(let x=0;x<canvas.width;x++){
    const index=(y*canvas.width+x)*4,originalAlpha=source.data[index+3]!;
    let alpha=originalAlpha,colorIndex=index;
    for(const offset of offsets){
     const sx=x+offset.x,sy=y+offset.y;
     const neighbor=sx<0||sy<0||sx>=canvas.width||sy>=canvas.height?-1:(sy*canvas.width+sx)*4;
     const neighborAlpha=neighbor<0?0:source.data[neighbor+3]!;
     const candidate=originalAlpha+(neighborAlpha-originalAlpha)*offset.weight;
     if(change.widthDelta!>0?candidate>alpha:candidate<alpha){alpha=candidate;if(change.widthDelta!>0&&neighbor>=0)colorIndex=neighbor;}
    }
    output.data[index]=source.data[colorIndex]!;output.data[index+1]=source.data[colorIndex+1]!;output.data[index+2]=source.data[colorIndex+2]!;
    output.data[index+3]=Math.round(alpha);hasInk ||= output.data[index+3]!>0;
   }
   if(!hasInk)return anchor;
   context.putImageData(output,0,0);
  }
  if(change.color){context.globalCompositeOperation='source-in';context.fillStyle=change.color;context.fillRect(0,0,canvas.width,canvas.height);}
  const encoded=canvas.toDataURL('image/png');
  const base64=encoded.slice('data:image/png;base64,'.length);
  const bytes=base64.length*3/4-(base64.endsWith('==')?2:base64.endsWith('=')?1:0);
  if(bytes>LIMITS.maxSenderSignatureBytes)return anchor;
  return {...anchor,x:anchor.x-left*pixelWidth,y:anchor.y-top*pixelHeight,width:anchor.width+(left+right)*pixelWidth,height:anchor.height+(top+bottom)*pixelHeight,image:encoded};
 }catch{return anchor;}
}

/** Ink undo is invalidated by any external placement edit, so it cannot restore stale signer fields. */
export class InkUndoHistory {
 private expected:string;
 private entries:SignatureAnchor[][]=[];
 constructor(anchors:SignatureAnchor[]){this.expected=JSON.stringify(anchors);}
 observe(anchors:SignatureAnchor[]):boolean {const next=JSON.stringify(anchors);if(next!==this.expected){this.entries=[];this.expected=next;}return this.entries.length>0;}
 record(before:SignatureAnchor[],after:SignatureAnchor[]):void {this.observe(before);this.entries=[...this.entries.slice(-19),before];this.expected=JSON.stringify(after);}
 undo(current:SignatureAnchor[]):SignatureAnchor[]|null {if(!this.observe(current))return null;const previous=this.entries.pop()!;this.expected=JSON.stringify(previous);return previous;}
}
